import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar, Window, WindowActionsBar } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Grid, GridColumn as Column } from '@progress/kendo-react-grid';
import { Loader } from "@progress/kendo-react-indicators";
import { Checkbox, Input } from '@progress/kendo-react-inputs';
import { useState } from 'react';
import CenterDivPanel from '../../components/CenterDivPanel';
import { fetchApi } from '../../services/api';
import { isNullOrWhiteSpace } from '../../utils/utils';
import { contactTypeOptions } from './ContactTypeOptions';
import { CustomerContactType } from './CustomerContactProfile';
import { CreatingField } from 'components/CreatingField';
import { Label } from '@progress/kendo-react-labels';

type Props = {
  CustomerId: number;
  CloseDialog: (newCustomerContactId?: number) => void;
}

type DuplicateCustomerContact = {
  CustomerID: number;
  CustomerContactID: number;
  CustomerNumber: string;
  CustomerName: string;
  FullName: string;
  EMailAddress: string;
  PhoneNumber: string;
  CellNumber: string;
}

type AddCustomerContactRequest = CustomerContactType & {
  Force: boolean;
}

type AddCustomerContactResponse = {
  CustomerContactID: number;
  DuplicateCustomerContacts: DuplicateCustomerContact[];
}

const AddCustomerContactDialog = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [duplicateCustomerContacts, setDuplicateCustomerContacts] = useState<DuplicateCustomerContact[]>([]);
  const [contact, setContact] = useState<Partial<CustomerContactType>>({
    ContactType: contactTypeOptions[contactTypeOptions.length - 1].ID
  });

  const canSave = (isNullOrWhiteSpace(contact?.FirstName) === false && 
                   isNullOrWhiteSpace(contact?.LastName) === false && 
                   (isNullOrWhiteSpace(contact?.EmailAddress) === false || isNullOrWhiteSpace(contact?.PhoneNumber) === false));

  const _save = () => {
    if (canSave === false)
      return;

    setLoading(true);

    const data = {
      ...contact, CustomerID: props.CustomerId, Force: duplicateCustomerContacts.length > 0
    } as AddCustomerContactRequest


    fetchApi('/api/Customer/AddCustomerContact', data, 'POST')
      .then((data: AddCustomerContactResponse) => {
        setLoading(false);
        setDuplicateCustomerContacts(data.DuplicateCustomerContacts || []);
        if (data.CustomerContactID > 0) props.CloseDialog(data.CustomerContactID);
      }
      ).catch((e) => {
        setLoading(false);
        // If not problem details
        if (!e?.status) alert('Unable to create contact');
      });
  }

  return <Dialog title={'Create Customer Contact'} onClose={() => props.CloseDialog()} width={400} height={650}>
    {loading && <CenterDivPanel>
      <Loader type="converging-spinner" />
    </CenterDivPanel>}
    {duplicateCustomerContacts.length > 0 && <Window title="Possible Duplicate Contacts" onClose={() => setDuplicateCustomerContacts([])} width={950} height={600}>
      <Grid
        data={duplicateCustomerContacts}
        scrollable='none'
        onRowClick={(e) => props.CloseDialog(e.dataItem.CustomerContactID)}
      >
        <Column field="FullName" title="Contact" cells={{ data: (props) => <td {...props.tdProps} style={{ color: '#007bff' }} role="button">{props.dataItem.FullName}</td> }} />
        <Column field="EMailAddress" title="E-Mail" />
        <Column field="PhoneNumber" title="Office Number" />
        <Column field="CellNumber" title="Cell Number" />
        <Column field="CustomerNumber" title="Customer #" />
        <Column field="CustomerName" title="Customer Name" />
      </Grid>
      <WindowActionsBar>
        <Button onClick={() => setDuplicateCustomerContacts([])}>Close</Button>
        <Button themeColor="warning" disabled={canSave === false || loading} onClick={_save}>
          Create Contact Anyway
        </Button>
      </WindowActionsBar>
    </Window>}
    <div className="k-form k-form-md p-0">
      <div>
        <FieldWrapper>
          <Input
            required
            maxLength={20}
            autoFocus
            disabled={loading}
            value={contact?.FirstName}
            label="First Name"
            onChange={(e) => setContact({ ...contact, FirstName: e.value })}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Input
            required
            maxLength={20}
            disabled={loading}
            value={contact?.LastName}
            label="Last Name"
            onChange={(e) => setContact({ ...contact, LastName: e.value })}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Input
            type="email"
            maxLength={80}
            disabled={loading}
            label="E-Mail"
            required={isNullOrWhiteSpace(contact?.PhoneNumber)}
            value={contact?.EmailAddress}
            onChange={(e) => setContact({ ...contact, EmailAddress: e.value })}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Input
            maxLength={50}
            disabled={loading}
            value={contact?.Title}
            label="Title"
            onChange={(e) => setContact({ ...contact, Title: e.value })}
          />
        </FieldWrapper>
        <FieldWrapper>
          <DropDownList
            data={contactTypeOptions}
            dataItemKey="ID"
            textField="Name"
            label="Contact Type"
            disabled={loading}
            value={contactTypeOptions.find(contactType => contactType.ID === contact?.ContactType) ?? contactTypeOptions[contactTypeOptions.length - 1]}
            onChange={(e) => setContact({ ...contact, ContactType: e.target.value.ID })}
          />
        </FieldWrapper>
        <FieldWrapper>
          <div className="row">
            <div className="col-8 pr-1">
              <Label>Office Number</Label>
              <CreatingField
                dataType='intlphone'
                data={contact?.PhoneNumber}
                inputProps={{
                  disabled: loading
                }}
                required={isNullOrWhiteSpace(contact?.EmailAddress)}
                onChange={(e) => setContact({ ...contact, PhoneNumber: e })}
              />
            </div>
            <div className="col-4 pl-1">
              <Label>Ext.</Label>
              <Input
                maxLength={10}
                value={contact?.PhoneNumberExtension}
                disabled={loading}
                onChange={(e) => setContact({ ...contact, PhoneNumberExtension: e.value })}
              />
            </div>
          </div>
        </FieldWrapper>
        <FieldWrapper>
          <Label>Cell Number</Label>
          <CreatingField
            dataType='intlphone'
            data={contact?.CellNumber}
            inputProps={{
              disabled: loading
            }}
            onChange={(e) => setContact({ ...contact, CellNumber: e })}
          />
        </FieldWrapper>
        <FieldWrapper>
          <Checkbox
            label={"Receive E-mail Invoices"}
            onChange={(e) => setContact({ ...contact, EmailInvoices: e.value })}
          />
        </FieldWrapper>
      </div>
    </div>
    <DialogActionsBar>
      <Button onClick={() => props.CloseDialog()}>Cancel</Button>
      <Button themeColor="primary" disabled={canSave === false || loading} onClick={_save}>
        Create Contact
      </Button>
    </DialogActionsBar>
  </Dialog>
}

export default AddCustomerContactDialog;
